import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Image as ImageIcon, Edit3, User, Type, Palette, Badge, Sparkles, Lightbulb, Brush, Check, Minus, Plus, Loader2, Trash2 } from 'lucide-react';
import CachedImg from '../../components/CachedImg';
import GeneratedImagesInline from '../../components/GeneratedImagesInline';
import SubscriptionLimitModal from '../../components/SubscriptionLimitModal';
import LoadingAnimation from '../../components/LoadingAnimation';
import { GENERATE_AD_URL, GENERATE_AUTH_TOKEN } from '../../config';
import { supabase } from '../../lib/supabase';

type ProductImage = { id: string; url: string; principal: boolean; orden: number; variante_id: string };
type Variant = { id: string; nombre: string; images?: ProductImage[] };

type ScenarioKey = 'sugerido' | 'fondo_blanco' | 'personalizado' | 'biblioteca';
type FormatoKey = 'post' | 'story' | 'square';

export const PruebaNuevaInterfaz: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsParam = params.get('ids') || '';
  const ids = useMemo(() => idsParam.split(',').map(s => s.trim()).filter(Boolean), [idsParam]);
  const modeloMode = params.get('modelo_mode') as 'ia' | 'guardados' | 'personalizado' | null;
  const textoSelected = params.get('texto_enabled') === '1';
  const modeloBadgeText =
    modeloMode === 'guardados'
      ? 'Añadiendo modelo guardado'
      : modeloMode === 'personalizado'
        ? 'Añadiendo modelo personalizado'
        : modeloMode === 'ia'
          ? 'Añadiendo modelo automático'
          : null;

  const cantidadImagenesParam = params.get('cantidad_imagenes');
  const formatoParam = params.get('formato') as FormatoKey | null;
  const modoPro = params.get('modo_pro') === '1';

  const [variants, setVariants] = useState<Variant[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedScenarios, setSelectedScenarios] = useState<Record<ScenarioKey, boolean>>({
    sugerido: true,
    fondo_blanco: false,
    personalizado: false,
    biblioteca: false,
  });
  const [imageCount, setImageCount] = useState<number>(() => {
    const n = Number(cantidadImagenesParam);
    return Number.isFinite(n) && n > 0 ? n : 2;
  });
  const [format, setFormat] = useState<FormatoKey>(() => {
    if (formatoParam && ['post', 'story', 'square'].includes(formatoParam)) return formatoParam;
    return 'post';
  });
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [signedUrls, setSignedUrls] = useState<Record<string, { full: string; thumbnail: string }>>({});
  const [soonFeatureKey, setSoonFeatureKey] = useState<string | null>(null);
  const [deleteConfirmKey, setDeleteConfirmKey] = useState<string | null>(null);
  const [generating, setGenerating] = useState<boolean>(false);
  const [imagesReady, setImagesReady] = useState<boolean>(false);
  const [showLoadingScreen, setShowLoadingScreen] = useState<boolean>(false);
  const [loadingExiting, setLoadingExiting] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [generatedImageIds, setGeneratedImageIds] = useState<number[]>([]);
  const [companyIdForImages, setCompanyIdForImages] = useState<string>('');
  const [genError, setGenError] = useState<string | null>(null);
  const [showSubscriptionLimitModal, setShowSubscriptionLimitModal] = useState<boolean>(false);
  const [subscriptionLimitType, setSubscriptionLimitType] = useState<'free-trial-ended' | 'plan-limit-reached'>('free-trial-ended');
  const loadingImageKeysRef = useRef<Set<string>>(new Set());
  const defaultsInitializedRef = useRef(false);
  const [loadingDurationMs, setLoadingDurationMs] = useState<number>(80000);

  useEffect(() => {
    if (defaultsInitializedRef.current) return;
    defaultsInitializedRef.current = true;

    const qp = new URLSearchParams(location.search);
    let changed = false;

    if (!qp.get('escenario')) {
      qp.set('escenario', 'sugerido');
      changed = true;
    }
    if (!qp.get('formato') && format) {
      qp.set('formato', format);
      changed = true;
    }
    if (!qp.get('cantidad_imagenes') && imageCount > 0) {
      qp.set('cantidad_imagenes', String(imageCount));
      changed = true;
    }

    if (changed) {
      navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
    }
  }, [location.pathname, location.search, navigate, format, imageCount]);

  const formatOptions: Array<{ id: FormatoKey; name: string; image: string }> = [
    {
      id: 'post',
      name: 'Posteo (4:5)',
      image:
        'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/formatos/Formato_Posteo.jpeg',
    },
    {
      id: 'story',
      name: 'Historias/Reels (9:16)',
      image:
        'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/formatos/Formato_Historia.jpeg',
    },
    {
      id: 'square',
      name: 'Cuadrado (1:1)',
      image:
        'https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/formatos/Formato_Cuadrado.jpeg',
    },
  ];

  const checkSubscriptionLimits = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No company associated with user');

      const { data: companyProfile } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes, paid_plan_start_date')
        .eq('id_company', profile.id_company)
        .single();

      if (!companyProfile) throw new Error('No company profile found');

      const remaining = (companyProfile as any).imagenes_restantes as number | null | undefined;
      if (typeof remaining === 'number' && remaining <= 0) {
        const isFree = (companyProfile as any).plan === 'gratis';
        setSubscriptionLimitType(isFree ? 'free-trial-ended' : 'plan-limit-reached');
        setShowSubscriptionLimitModal(true);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Error checking subscription limits:', err);
      return true;
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { if (mounted) { setVariants([]); setLoading(false); } return; }
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();
        const idCompany: string | null = (profile as any)?.id_company ?? null;
        if (!idCompany || ids.length === 0) { if (mounted) { setVariants([]); setLoading(false); } return; }
        if (mounted) setCompanyId(idCompany);
        // 1) Fetch basic variants
        const { data: variantsData } = await supabase
          .from('variantes')
          .select('id, nombre')
          .eq('id_company', idCompany)
          .in('id', ids);
        const vd = (variantsData || []) as any[];
        // 2) Fetch product images for these variants
        const variantIds = vd.map(v => v.id);
        let imagesByVariantId: Record<string, ProductImage[]> = {};
        if (variantIds.length > 0) {
          const { data: imagesData } = await supabase
            .from('productos_imagenes')
            .select('id, url, principal, orden, variante_id')
            .eq('id_company', idCompany)
            .in('variante_id', variantIds);
          imagesByVariantId = {};
          (imagesData || []).forEach((img: any) => {
            const entry: ProductImage = {
              id: String(img.id),
              url: String(img.url || ''),
              principal: !!img.principal,
              orden: Number(img.orden || 0),
              variante_id: String(img.variante_id || ''),
            };
            if (!imagesByVariantId[entry.variante_id]) imagesByVariantId[entry.variante_id] = [];
            imagesByVariantId[entry.variante_id].push(entry);
          });
        }
        const processed: Variant[] = vd.map(v => ({ id: String(v.id), nombre: String(v.nombre || 'Producto'), images: imagesByVariantId[String(v.id)] || [] }));
        if (mounted) setVariants(processed);
      } catch {
        if (mounted) setVariants([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [ids]);

  const principalImageFor = (v: Variant): ProductImage | undefined => {
    const imgs = v.images || [];
    return imgs.find(i => i.principal) || imgs[0];
  };

  const principalImageUrl = (v: Variant): string | null => {
    const img = principalImageFor(v);
    if (!img?.url) return null;
    const key = img.url;
    const signed = signedUrls[key];
    if (signed?.thumbnail) return signed.thumbnail;
    if (key.startsWith('http')) return key; // fallback while signing not ready
    return null;
  };

  const ensurePrincipalImageSigned = async (v: Variant) => {
    const img = principalImageFor(v);
    if (!img?.url || !companyId) return;
    const key = img.url as string;
    if (key.startsWith('http')) {
      // Try to derive filename and sign opt/full paths from storage
      const fileNameWithQuery = key.split('/').pop() || '';
      const fileName = fileNameWithQuery.split('?')[0];
      if (!fileName) {
        if (!signedUrls[key]) setSignedUrls(prev => ({ ...prev, [key]: { full: key, thumbnail: key } }));
        return;
      }
      const pathVariantId = (img as any)?.variante_id ?? v.id;
      const optFilePath = `${companyId}/${pathVariantId}/opt/${fileName}`;
      const fullFilePath = `${companyId}/${pathVariantId}/full/${fileName}`;
      try {
        let thumbSigned: string | null = null;
        let fullSigned: string | null = null;
        const { data: thumb } = await supabase.storage.from('productos').createSignedUrl(optFilePath, 3600);
        if (thumb?.signedUrl) {
          thumbSigned = thumb.signedUrl;
          const { data: full } = await supabase.storage.from('productos').createSignedUrl(fullFilePath, 3600);
          fullSigned = full?.signedUrl || thumbSigned;
        } else {
          const { data: full } = await supabase.storage.from('productos').createSignedUrl(fullFilePath, 3600);
          if (full?.signedUrl) {
            thumbSigned = full.signedUrl;
            fullSigned = full.signedUrl;
          }
        }
        setSignedUrls(prev => ({ ...prev, [key]: { full: fullSigned || key, thumbnail: thumbSigned || fullSigned || key } }));
      } catch {
        if (!signedUrls[key]) setSignedUrls(prev => ({ ...prev, [key]: { full: key, thumbnail: key } }));
      }
      return;
    }
    if (signedUrls[key] || loadingImageKeysRef.current.has(key)) return;
    loadingImageKeysRef.current.add(key);
    try {
      const pathVariantId = (img as any)?.variante_id ?? v.id;
      const optFilePath = `${companyId}/${pathVariantId}/opt/${key}`;
      const fullFilePath = `${companyId}/${pathVariantId}/full/${key}`;
      let thumbSigned: string | null = null;
      let fullSigned: string | null = null;
      const { data: thumb } = await supabase.storage.from('productos').createSignedUrl(optFilePath, 3600);
      if (thumb?.signedUrl) {
        thumbSigned = thumb.signedUrl;
        const { data: full } = await supabase.storage.from('productos').createSignedUrl(fullFilePath, 3600);
        fullSigned = full?.signedUrl || thumbSigned;
      } else {
        const { data: full } = await supabase.storage.from('productos').createSignedUrl(fullFilePath, 3600);
        if (full?.signedUrl) {
          thumbSigned = full.signedUrl;
          fullSigned = full.signedUrl;
        }
      }
      setSignedUrls(prev => ({ ...prev, [key]: { full: fullSigned || key, thumbnail: thumbSigned || fullSigned || key } }));
    } catch {
      setSignedUrls(prev => ({ ...prev, [key]: { full: '', thumbnail: '' } }));
    } finally {
      loadingImageKeysRef.current.delete(key);
    }
  };

  useEffect(() => {
    // Pre-sign principal images for loaded variants
    if (!companyId || variants.length === 0) return;
    variants.forEach(v => { void ensurePrincipalImageSigned(v); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, variants.map(v => v.id).join(',' )]);

  const toggleScenario = (key: ScenarioKey) => {
    const wasPersonalizadoSelected = selectedScenarios.personalizado;
    const wasBibliotecaSelected = selectedScenarios.biblioteca;
    const wasKeySelected = selectedScenarios[key];

    setSelectedScenarios(prev => {
      const isSelected = prev[key];
      if (isSelected) {
        return { sugerido: false, fondo_blanco: false, personalizado: false, biblioteca: false };
      }
      return {
        sugerido: key === 'sugerido',
        fondo_blanco: key === 'fondo_blanco',
        personalizado: key === 'personalizado',
        biblioteca: key === 'biblioteca',
      };
    });

    const qp = new URLSearchParams(location.search);

    if (wasKeySelected) {
      qp.delete('escenario');
      navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
      return;
    }

    qp.set('escenario', key);

    if (key === 'personalizado' && !wasPersonalizadoSelected) {
      navigate(`/redes-sociales/crear-anuncio/escenario-personalizado?${qp.toString()}`);
      return;
    }
    if (key === 'biblioteca' && !wasBibliotecaSelected) {
      qp.set('estudio_virtual', '1');
      navigate(`/redes-sociales/crear-anuncio/biblioteca-escenarios-estudio-virtual?${qp.toString()}`);
      return;
    }

    navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
  };

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const esc = p.get('escenario') as ScenarioKey | null;
    if (!esc) return;
    setSelectedScenarios({
      sugerido: esc === 'sugerido',
      fondo_blanco: esc === 'fondo_blanco',
      personalizado: esc === 'personalizado',
      biblioteca: esc === 'biblioteca',
    });
  }, [location.search]);

  const addOptions = useMemo(
    () => [
      { key: 'modelo', title: 'Modelo', Icon: User },
      { key: 'texto', title: 'Texto', Icon: Type },
      { key: 'colores', title: 'Colores', Icon: Palette },
      { key: 'logo', title: 'Logo', Icon: Badge },
    ],
    [],
  );

  const handleAddOptionClick = (key: string) => {
    const qp = new URLSearchParams(location.search);
    if (ids.length > 0) qp.set('ids', ids.join(',')); else qp.delete('ids');
    qp.set('estudio_virtual', '1');
    const query = qp.toString();

    if (key === 'modelo') {
      navigate(`/redes-sociales/crear-anuncio/personalizar-modelo-estudio-virtual?${query}`);
      return;
    }
    if (key === 'texto') {
      navigate(`/redes-sociales/crear-anuncio/seleccionar-texto-estudio-virtual?${query}`);
      return;
    }
    if (key === 'colores' || key === 'logo') {
      setSoonFeatureKey(key);
    }
  };

  const handleRemoveAddOption = (key: string) => {
    const qp = new URLSearchParams(location.search);
    
    if (key === 'modelo') {
      // Remove all model-related params
      qp.delete('modelo_mode');
      qp.delete('modelo_guardado_id');
      qp.delete('modelo_guardado_nombre');
      qp.delete('genero');
      qp.delete('edad');
      qp.delete('tipo_cuerpo');
      qp.delete('tono_piel');
      qp.delete('color_pelo');
      qp.delete('largo_pelo');
      qp.delete('desc_modelo');
      qp.delete('pose');
    }
    if (key === 'texto') {
      qp.delete('texto_enabled');
      qp.delete('texto_comp_id');
    }
    
    navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
  };

  // Preload images by fetching signed URLs and loading them into browser cache
  // Returns preloaded image data to pass to the results page
  type PreloadedImage = { id: number; fileName: string; signedUrl: string; favorito: boolean };
  const preloadGeneratedImages = async (imageIds: number[], companyId: string): Promise<PreloadedImage[]> => {
    const maxAttempts = 8;
    const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));
    const pending = new Set<number>(imageIds);
    const preloadedImages: PreloadedImage[] = [];

    // Step 1: Get signed URLs for all images (with retry logic)
    for (let attempt = 1; attempt <= maxAttempts && pending.size > 0; attempt++) {
      for (const imageId of Array.from(pending)) {
        try {
          const { data: imageData, error: imageError } = await supabase
            .from('imagenes_generadas')
            .select('file_name, favorito')
            .eq('id', imageId)
            .eq('id_company', companyId)
            .single();

          if (imageError || !imageData?.file_name) {
            continue;
          }

          const filePath = `${companyId}/full/${imageData.file_name}`;
          const { data: signedUrlData } = await supabase.storage.from('generadas').createSignedUrl(filePath, 3600);
          
          if (signedUrlData?.signedUrl) {
            preloadedImages.push({
              id: imageId,
              fileName: imageData.file_name,
              signedUrl: signedUrlData.signedUrl,
              favorito: imageData.favorito === true,
            });
            pending.delete(imageId);
          }
        } catch (err) {
          console.error(`[preloadGeneratedImages] Error for image ${imageId}:`, err);
        }
      }

      if (pending.size > 0 && attempt < maxAttempts) {
        await delay(1000);
      }
    }

    // Step 2: Preload images into browser cache
    await Promise.all(
      preloadedImages.map(
        (img) =>
          new Promise<void>((resolve) => {
            const image = new Image();
            image.onload = () => resolve();
            image.onerror = () => resolve(); // Don't fail on error, just continue
            image.src = img.signedUrl;
          })
      )
    );

    return preloadedImages;
  };

  const handleGenerate = async () => {
    if (ids.length === 0) return;

    try {
      setGenError(null);
      setGenerating(true);
      setImagesReady(false);
      setShowLoadingScreen(true);
      setLoadingExiting(false);
      const canGenerate = await checkSubscriptionLimits();
      if (!canGenerate) {
        setGenerating(false);
        setShowLoadingScreen(false);
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No se encontró la empresa del usuario');

      let formatName = '';
      switch (format) {
        case 'story':
          formatName = '9:16';
          break;
        case 'square':
          formatName = '1:1';
          break;
        case 'post':
        default:
          formatName = '4:5';
      }

      const textoEnabledFlag = params.get('texto_enabled') === '1';
      const textoCompIdParam = params.get('texto_comp_id') || '';
      const textoCompUrl = params.get('texto_comp_url') || '';
      const textoTextParam = params.get('texto_texto') || '';
      const textoCompId = textoCompIdParam ? Number(textoCompIdParam) : NaN;

      const pose = params.get('pose') || '';
      const rawModeloMode = params.get('modelo_mode') as 'ia' | 'guardados' | 'personalizado' | null;
      const modeloModeParam =
        rawModeloMode === 'ia' || rawModeloMode === 'guardados' || rawModeloMode === 'personalizado'
          ? rawModeloMode
          : null;
      const modeloGuardadoId = params.get('modelo_guardado_id') || '';
      const modeloGuardadoNombre = params.get('modelo_guardado_nombre') || '';
      const genero = params.get('genero') || '';
      const edad = params.get('edad') || '';
      const tipoCuerpo = params.get('tipo_cuerpo') || '';
      const tonoPiel = params.get('tono_piel') || '';
      const colorPelo = params.get('color_pelo') || '';
      const largoPelo = params.get('largo_pelo') || '';
      const descModelo = params.get('desc_modelo') || '';

      const escenarioDescParam = params.get('escenario_desc') || '';
      const escenarioFondoIdParam = params.get('escenario_fondo_id') || '';
      const escenarioFondoUrlParam = params.get('escenario_fondo_url') || '';
      const escenarioParam = params.get('escenario') as ScenarioKey | null;

      let scenario: ScenarioKey = 'sugerido';
      if (selectedScenarios.sugerido) scenario = 'sugerido';
      else if (selectedScenarios.fondo_blanco) scenario = 'fondo_blanco';
      else if (selectedScenarios.personalizado) scenario = 'personalizado';
      else if (selectedScenarios.biblioteca) scenario = 'biblioteca';
      else if (escenarioParam) scenario = escenarioParam;

      const isAutomaticScenario = scenario === 'sugerido';
      setLoadingDurationMs(isAutomaticScenario ? 80000 : 35000);

      let backgroundMode: 'ia' | 'blanco' | 'elegido' | 'descripcion' = 'ia';
      let backgroundId: number | undefined;
      let backgroundUrl: string | undefined;
      let backgroundText: string | undefined;

      switch (scenario) {
        case 'fondo_blanco':
          backgroundMode = 'blanco';
          break;
        case 'biblioteca': {
          backgroundMode = 'elegido';
          const n = Number(escenarioFondoIdParam);
          if (Number.isFinite(n)) backgroundId = n;
          if (escenarioFondoUrlParam) backgroundUrl = escenarioFondoUrlParam;
          break;
        }
        case 'personalizado':
          backgroundMode = 'descripcion';
          if (escenarioDescParam.trim()) backgroundText = escenarioDescParam.trim();
          break;
        case 'sugerido':
        default:
          backgroundMode = 'ia';
      }

      const variantIds = ids
        .map(s => String(s))
        .map(s => s.trim())
        .filter(s => !!s && s !== '0');
      if (variantIds.length === 0) throw new Error('No hay productos válidos seleccionados');
      const mode = variantIds.length > 1 ? 'multiple' : 'single';

      const modoForApi: 'rapido' | 'personalizado' =
        scenario === 'sugerido' ? 'rapido' : 'personalizado';

      let composicion: 'modelo' | 'producto' = 'producto';
      if (modeloModeParam === 'ia' || modeloModeParam === 'guardados' || modeloModeParam === 'personalizado') {
        composicion = 'modelo';
      }

      const requestData: Record<string, any> = {
        user_id: user.id,
        variante_id: variantIds,
        mode,
        modo: modoForApi,
        cantidad_imagenes: imageCount,
        Format: formatName,
        composicion,
        background_mode: backgroundMode,
      };

      if (backgroundMode === 'elegido') {
        if (typeof backgroundId === 'number' && Number.isFinite(backgroundId)) {
          requestData.background_id = backgroundId;
        }
        if (!requestData.background_id && backgroundUrl) {
          requestData.background_url = backgroundUrl;
        }
      } else if (backgroundMode === 'blanco') {
        requestData.background_id = 5;
      } else if (backgroundMode === 'descripcion' && backgroundText) {
        requestData.background_text = backgroundText;
      }

      if (textoEnabledFlag) {
        requestData.composition_mode = true;
        if (Number.isFinite(textoCompId)) {
          requestData.composition_id = textoCompId;
        } else if (textoCompUrl) {
          requestData.composition_url = textoCompUrl;
        }
        if (textoTextParam.trim()) {
          requestData.composition_text = textoTextParam.trim();
        }
      }

      if (pose) requestData.pose = pose;
      if (modeloModeParam) {
        requestData.modelo_mode = modeloModeParam;
        if (modeloModeParam === 'guardados' && modeloGuardadoId) {
          requestData.modelo_guardado_id = modeloGuardadoId;
          if (modeloGuardadoNombre) requestData.modelo_guardado_nombre = modeloGuardadoNombre;
        } else if (modeloModeParam === 'personalizado') {
          if (genero) requestData.genero = genero;
          if (edad) requestData.edad = edad;
          if (tipoCuerpo) requestData.tipo_cuerpo = tipoCuerpo;
          if (tonoPiel) requestData.tono_piel = tonoPiel;
          if (colorPelo) requestData.color_pelo = colorPelo;
          if (largoPelo) requestData.largo_pelo = largoPelo;
          if (descModelo.trim()) requestData.desc_modelo = descModelo.trim();
        }
      }

      requestData.modo_pro = modoPro;

      try { console.log('[PruebaNuevaInterfaz] handleGenerate payload', requestData); } catch {}

      const response = await fetch(GENERATE_AD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify(requestData),
      });

      let respText: string | null = null;
      try { respText = await response.clone().text(); } catch {}
      try { console.log('[PruebaNuevaInterfaz] handleGenerate response', { status: response.status, ok: response.ok, text: respText }); } catch {}

      if (!response.ok) {
        throw new Error(respText || `Error ${response.status}`);
      }

      let data: any = null;
      try { data = await response.json(); } catch (e) { console.error('[PruebaNuevaInterfaz] handleGenerate json parse error', e); }

      if (data?.response === 'success' && Array.isArray(data?.imagenes_generadas_ids) && data.imagenes_generadas_ids.length > 0) {
        const imageIds = data.imagenes_generadas_ids as number[];
        const companyIdResult = (data.id_company as string) || profile.id_company;
        setGeneratedImageIds(imageIds);
        setCompanyIdForImages(companyIdResult);
        setGenError(null);
        try { window.sessionStorage.setItem('atlas:crear-anuncio:has-generated', '1'); } catch {}
        // Build lastPayload without user_id for reuse in "generate more"
        const { user_id: _uid, ...lastPayload } = requestData;
        
        // Preload images before navigating to ensure smooth animation
        let preloadedImages: PreloadedImage[] = [];
        try {
          preloadedImages = await preloadGeneratedImages(imageIds, companyIdResult);
        } catch (preloadErr) {
          console.warn('[PruebaNuevaInterfaz] Image preload failed, continuing anyway:', preloadErr);
        }
        
        // Navigate to dedicated generated images page with preloaded data
        navigate('/redes-sociales/imagenes-generadas', {
          state: { imageIds, companyId: companyIdResult, lastPayload, preloadedImages }
        });
      } else {
        throw new Error('No se recibieron imágenes generadas.');
      }
    } catch (e: any) {
      console.error('[PruebaNuevaInterfaz] handleGenerate error', e);
      setGenError(e?.message || 'Se produjo un error. No se consumieron imágenes. Por favor, volvé a intentarlo.');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateMore = async () => {
    if (ids.length === 0) return;
    try {
      setGenError(null);
      setGenerating(true);
      setImagesReady(false);
      setShowLoadingScreen(true);
      setLoadingExiting(false);
      const canGenerate = await checkSubscriptionLimits();
      if (!canGenerate) {
        setGenerating(false);
        setShowLoadingScreen(false);
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No se encontró la empresa del usuario');

      let formatName = '';
      switch (format) {
        case 'story':
          formatName = '9:16';
          break;
        case 'square':
          formatName = '1:1';
          break;
        case 'post':
        default:
          formatName = '4:5';
      }

      const textoEnabledFlag = params.get('texto_enabled') === '1';
      const textoCompIdParam = params.get('texto_comp_id') || '';
      const textoCompUrl = params.get('texto_comp_url') || '';
      const textoTextParam = params.get('texto_texto') || '';
      const textoCompId = textoCompIdParam ? Number(textoCompIdParam) : NaN;

      const pose = params.get('pose') || '';
      const rawModeloModeMore = params.get('modelo_mode') as 'ia' | 'guardados' | 'personalizado' | null;
      const modeloModeParam =
        rawModeloModeMore === 'ia' || rawModeloModeMore === 'guardados' || rawModeloModeMore === 'personalizado'
          ? rawModeloModeMore
          : null;
      const modeloGuardadoId = params.get('modelo_guardado_id') || '';
      const modeloGuardadoNombre = params.get('modelo_guardado_nombre') || '';
      const genero = params.get('genero') || '';
      const edad = params.get('edad') || '';
      const tipoCuerpo = params.get('tipo_cuerpo') || '';
      const tonoPiel = params.get('tono_piel') || '';
      const colorPelo = params.get('color_pelo') || '';
      const largoPelo = params.get('largo_pelo') || '';
      const descModelo = params.get('desc_modelo') || '';

      const escenarioDescParam = params.get('escenario_desc') || '';
      const escenarioFondoIdParam = params.get('escenario_fondo_id') || '';
      const escenarioFondoUrlParam = params.get('escenario_fondo_url') || '';
      const escenarioParam = params.get('escenario') as ScenarioKey | null;

      let scenario: ScenarioKey = 'sugerido';
      if (selectedScenarios.sugerido) scenario = 'sugerido';
      else if (selectedScenarios.fondo_blanco) scenario = 'fondo_blanco';
      else if (selectedScenarios.personalizado) scenario = 'personalizado';
      else if (selectedScenarios.biblioteca) scenario = 'biblioteca';
      else if (escenarioParam) scenario = escenarioParam;

      let backgroundMode: 'ia' | 'blanco' | 'elegido' | 'descripcion' = 'ia';
      let backgroundId: number | undefined;
      let backgroundUrl: string | undefined;
      let backgroundText: string | undefined;

      switch (scenario) {
        case 'fondo_blanco':
          backgroundMode = 'blanco';
          break;
        case 'biblioteca': {
          backgroundMode = 'elegido';
          const n = Number(escenarioFondoIdParam);
          if (Number.isFinite(n)) backgroundId = n;
          if (escenarioFondoUrlParam) backgroundUrl = escenarioFondoUrlParam;
          break;
        }
        case 'personalizado':
          backgroundMode = 'descripcion';
          if (escenarioDescParam.trim()) backgroundText = escenarioDescParam.trim();
          break;
        case 'sugerido':
        default:
          backgroundMode = 'ia';
      }

      const variantIds = ids
        .map(s => String(s))
        .map(s => s.trim())
        .filter(s => !!s && s !== '0');
      if (variantIds.length === 0) throw new Error('No hay productos válidos seleccionados');
      const mode = variantIds.length > 1 ? 'multiple' : 'single';

      const modoForApi: 'rapido' | 'personalizado' =
        scenario === 'sugerido' ? 'rapido' : 'personalizado';

      let composicion: 'modelo' | 'producto' = 'producto';
      if (modeloModeParam === 'ia' || modeloModeParam === 'guardados' || modeloModeParam === 'personalizado') {
        composicion = 'modelo';
      }

      const requestData: Record<string, any> = {
        user_id: user.id,
        variante_id: variantIds,
        mode,
        modo: modoForApi,
        cantidad_imagenes: 2,
        Format: formatName,
        composicion,
        background_mode: backgroundMode,
      };

      if (backgroundMode === 'elegido') {
        if (typeof backgroundId === 'number' && Number.isFinite(backgroundId)) {
          requestData.background_id = backgroundId;
        }
        if (!requestData.background_id && backgroundUrl) {
          requestData.background_url = backgroundUrl;
        }
      } else if (backgroundMode === 'blanco') {
        requestData.background_id = 5;
      } else if (backgroundMode === 'descripcion' && backgroundText) {
        requestData.background_text = backgroundText;
      }

      if (textoEnabledFlag) {
        requestData.composition_mode = true;
        if (Number.isFinite(textoCompId)) {
          requestData.composition_id = textoCompId;
        } else if (textoCompUrl) {
          requestData.composition_url = textoCompUrl;
        }
        if (textoTextParam.trim()) {
          requestData.composition_text = textoTextParam.trim();
        }
      }

      if (pose) requestData.pose = pose;
      if (modeloModeParam) {
        requestData.modelo_mode = modeloModeParam;
        if (modeloModeParam === 'guardados' && modeloGuardadoId) {
          requestData.modelo_guardado_id = modeloGuardadoId;
          if (modeloGuardadoNombre) requestData.modelo_guardado_nombre = modeloGuardadoNombre;
        } else if (modeloModeParam === 'personalizado') {
          if (genero) requestData.genero = genero;
          if (edad) requestData.edad = edad;
          if (tipoCuerpo) requestData.tipo_cuerpo = tipoCuerpo;
          if (tonoPiel) requestData.tono_piel = tonoPiel;
          if (colorPelo) requestData.color_pelo = colorPelo;
          if (largoPelo) requestData.largo_pelo = largoPelo;
          if (descModelo.trim()) requestData.desc_modelo = descModelo.trim();
        }
      }

      requestData.modo_pro = modoPro;

      try { console.log('[PruebaNuevaInterfaz] handleGenerateMore payload', requestData); } catch {}

      const response = await fetch(GENERATE_AD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify(requestData),
      });

      let respText: string | null = null;
      try { respText = await response.clone().text(); } catch {}
      try { console.log('[PruebaNuevaInterfaz] handleGenerateMore response', { status: response.status, ok: response.ok, text: respText }); } catch {}

      if (!response.ok) {
        throw new Error(respText || `Error ${response.status}`);
      }

      let data: any = null;
      try { data = await response.json(); } catch (e) { console.error('[PruebaNuevaInterfaz] handleGenerateMore json parse error', e); }

      if (data?.response === 'success' && Array.isArray(data?.imagenes_generadas_ids) && data.imagenes_generadas_ids.length > 0) {
        const newImageIds = data.imagenes_generadas_ids as number[];
        const companyIdResult = (data.id_company as string) || profile.id_company;
        const allImageIds = [...generatedImageIds, ...newImageIds];
        
        // Preload only the NEW images before navigating
        let preloadedImages: PreloadedImage[] = [];
        try {
          preloadedImages = await preloadGeneratedImages(newImageIds, companyIdResult);
        } catch (preloadErr) {
          console.warn('[PruebaNuevaInterfaz] handleGenerateMore preload failed:', preloadErr);
        }
        
        setGeneratedImageIds(allImageIds);
        setCompanyIdForImages(companyIdResult);
        setGenError(null);
        try { window.sessionStorage.setItem('atlas:crear-anuncio:has-generated', '1'); } catch {}
        // Navigate to dedicated generated images page with preloaded images
        navigate('/redes-sociales/imagenes-generadas', {
          state: { imageIds: allImageIds, companyId: companyIdResult, preloadedImages }
        });
      } else {
        throw new Error('No se recibieron imágenes generadas.');
      }
    } catch (e: any) {
      console.error('[PruebaNuevaInterfaz] handleGenerateMore error', e);
      setGenError(e?.message || 'Se produjo un error. No se consumieron imágenes. Por favor, volvé a intentarlo.');
    } finally {
      setGenerating(false);
    }
  };

  const handleReturnToConfig = () => {
    setShowResults(false);
  };

  return (
    <div
      className="p-4 sm:p-8 max-w-4xl mx-auto min-h-[100dvh] md:min-h-screen bg-white sm:bg-transparent pb-48 sm:pb-0"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 168px)' }}
    >
      {/* Mobile sticky header - aligned with menu button (top-4 = 16px, h-10 = 40px) */}
      {!showResults && (
        <>
          {/* Gradient background overlay */}
          <div 
            className="sm:hidden fixed top-0 left-0 right-0 h-20 z-30 pointer-events-none"
            style={{ background: 'linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 50%, rgba(255,255,255,0) 100%)' }}
          />
          <div className="sm:hidden fixed top-4 left-0 right-0 z-40 flex items-center h-10 pointer-events-none">
            <h1 className="absolute left-1/2 -translate-x-1/2 text-sm font-semibold text-gray-900">Diseña tus imágenes</h1>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating || ids.length === 0}
              className={`pointer-events-auto absolute right-4 inline-flex items-center justify-center px-4 h-10 rounded-full shadow-sm text-sm ${
                generating || ids.length === 0
                  ? 'bg-gray-300 text-white cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              {generating ? 'Generando…' : 'Generar'}
            </button>
          </div>
        </>
      )}

      {!showResults ? (
        <div className="sm:bg-white sm:rounded-2xl sm:shadow-sm p-0 sm:p-6 mt-14 sm:mt-0">

          <div className="hidden sm:flex items-center justify-between mb-6">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Diseña tus imágenes</h1>
            </div>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating || ids.length === 0}
              className={`inline-flex items-center justify-center gap-2 px-5 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm ${
                generating || ids.length === 0
                  ? 'bg-gray-300 text-white cursor-not-allowed'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generando
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Generar imágenes
                </>
              )}
            </button>
          </div>

          {genError && (
            <div className="mb-4 text-sm text-red-600">
              {genError}
            </div>
          )}

        <div className="mt-12 sm:mt-6">
          <h2 className="text-sm font-medium text-gray-500 mb-2 ml-2">Productos seleccionados</h2>
          <div className="flex items-center gap-3 overflow-x-auto pb-1">
            {loading && (
              <div className="text-sm text-gray-600">Cargando...</div>
            )}
            {!loading && variants.length === 0 && (
              <div className="text-sm text-gray-600">No hay productos seleccionados</div>
            )}
            {!loading && variants.map(v => {
              const url = principalImageUrl(v);
              return (
                <div key={v.id} className="shrink-0 w-20 h-20 rounded-xl border border-gray-200 overflow-hidden bg-gray-100">
                  {url ? (
                    <img src={url} alt={v.nombre} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <ImageIcon className="w-6 h-6" />
                    </div>
                  )}
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => {
                const qp = new URLSearchParams();
                if (ids.length > 0) qp.set('ids', ids.join(','));
                const query = qp.toString();
                navigate(query ? `/redes-sociales/crear-anuncio/seleccionar-productos?${query}` : '/redes-sociales/crear-anuncio/seleccionar-productos');
              }}
              className="self-center inline-flex items-center gap-2 px-4 h-10 rounded-full border border-gray-200 bg-white text-gray-900 shadow-sm hover:bg-gray-50 text-sm shrink-0"
            >
              Cambiar
            </button>
          </div>
        </div>

        <div className="mt-6">
          <h2 className="text-sm font-medium text-gray-500 mb-3 ml-2">Escena</h2>
          <div>
            <div className="grid grid-cols-2 gap-3">
              {/* Automático */}
              <button
                type="button"
                onClick={() => toggleScenario('sugerido')}
                className={`relative flex flex-col items-start rounded-2xl border text-left text-xs sm:text-sm p-3 h-28 sm:h-32 transition-colors ${
                  selectedScenarios.sugerido ? 'border-purple-500 bg-purple-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className="shrink-0 bg-purple-50 rounded-md p-1.5">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                  </div>
                  {selectedScenarios.sugerido && (
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-purple-500 text-white text-[10px] ml-auto">
                      ✓
                    </span>
                  )}
                </div>
                <div className="mt-2 sm:mt-3">
                  <p className="text-gray-900 font-medium text-xs sm:text-sm">Automático</p>
                  <p className="mt-1 text-[11px] text-gray-600 sm:hidden">
                    Dejá que la IA decida
                  </p>
                  <p className="mt-1 text-[11px] text-gray-600 hidden sm:block">
                    La IA elegirá automáticamente una escena adecuada para tus productos.
                  </p>
                </div>
              </button>

              {/* Personalizado */}
              <button
                type="button"
                onClick={() => toggleScenario('personalizado')}
                className={`relative flex flex-col items-start rounded-2xl border text-left text-xs sm:text-sm p-3 h-28 sm:h-32 transition-colors ${
                  selectedScenarios.personalizado ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className="shrink-0 bg-emerald-50 rounded-md p-1.5">
                    <Edit3 className="w-4 h-4 text-emerald-600" />
                  </div>
                  {selectedScenarios.personalizado && (
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500 text-white text-[10px] ml-auto">
                      ✓
                    </span>
                  )}
                </div>
                <div className="mt-2 sm:mt-3">
                  <p className="text-gray-900 font-medium text-xs sm:text-sm">Personalizado</p>
                  <p className="mt-1 text-[11px] text-gray-600 sm:hidden">
                    Describí la imagen que querés
                  </p>
                  <p className="mt-1 text-[11px] text-gray-600 hidden sm:block">
                    Personalizá una escena exactamente como la querés.
                  </p>
                </div>
              </button>

              {/* Fondo blanco */}
              <button
                type="button"
                onClick={() => toggleScenario('fondo_blanco')}
                className={`relative flex flex-col items-start rounded-2xl border text-left text-xs sm:text-sm p-3 h-28 sm:h-32 transition-colors ${
                  selectedScenarios.fondo_blanco ? 'border-gray-500 bg-gray-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className="shrink-0 bg-gray-100 rounded-md p-1.5">
                    <Brush className="w-4 h-4 text-gray-600" />
                  </div>
                  {selectedScenarios.fondo_blanco && (
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-500 text-white text-[10px] ml-auto">
                      ✓
                    </span>
                  )}
                </div>
                <div className="mt-2 sm:mt-3">
                  <p className="text-gray-900 font-medium text-xs sm:text-sm">Fondo blanco</p>
                  <p className="mt-1 text-[11px] text-gray-600 sm:hidden">
                    Simple y minimalista
                  </p>
                  <p className="mt-1 text-[11px] text-gray-600 hidden sm:block">
                    Muestra tus productos sobre un fondo blanco limpio y minimalista.
                  </p>
                </div>
              </button>

              {/* Biblioteca de escenas */}
              <button
                type="button"
                onClick={() => toggleScenario('biblioteca')}
                className={`relative flex flex-col items-start rounded-2xl border text-left text-xs sm:text-sm p-3 h-28 sm:h-32 transition-colors ${
                  selectedScenarios.biblioteca ? 'border-amber-500 bg-amber-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className="shrink-0 bg-amber-50 rounded-md p-1.5">
                    <Lightbulb className="w-4 h-4 text-amber-600" />
                  </div>
                  {selectedScenarios.biblioteca && (
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-500 text-white text-[10px] ml-auto">
                      ✓
                    </span>
                  )}
                </div>
                <div className="mt-2 sm:mt-3">
                  <p className="text-gray-900 font-medium text-xs sm:text-sm">Biblioteca de escenas</p>
                  <p className="mt-1 text-[11px] text-gray-600 sm:hidden">
                    Explorá nuestras escenas predefinidas
                  </p>
                  <p className="mt-1 text-[11px] text-gray-600 hidden sm:block">
                    Elegí una escena desde una biblioteca predefinida.
                  </p>
                </div>
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <h2 className="text-sm font-medium text-gray-500 mb-3 ml-2">Elementos para añadir</h2>
          {soonFeatureKey && (
            <div className="mx-2 mb-3 rounded-xl bg-blue-50 text-blue-800 text-xs sm:text-sm px-3 py-2">
              Disponible muy pronto
            </div>
          )}
          <div className="flex items-stretch gap-0 pb-1">
            {addOptions.map(({ key, title, Icon }) => {
              const isModelo = key === 'modelo';
              const isTexto = key === 'texto';
              const badgeText = isModelo
                ? modeloBadgeText
                : isTexto && textoSelected
                  ? 'Añadiendo texto'
                  : null;
              const hasBadge = !!badgeText;
              const isInDeleteMode = deleteConfirmKey === key;
              const iconBgClass =
                key === 'colores' || key === 'logo' ? 'bg-slate-50'
                : 'bg-blue-50';
              const iconColorClass =
                key === 'colores' || key === 'logo' ? 'text-slate-600'
                : 'text-blue-600';
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    if (hasBadge && isInDeleteMode) {
                      // Second tap: remove the option
                      handleRemoveAddOption(key);
                      setDeleteConfirmKey(null);
                    } else if (hasBadge) {
                      // First tap on selected option: enter delete mode
                      setDeleteConfirmKey(key);
                    } else {
                      // Not selected: navigate to add
                      setDeleteConfirmKey(null);
                      handleAddOptionClick(key);
                    }
                  }}
                  className="flex-1 min-w-0 h-28 flex flex-col items-center justify-between rounded-xl hover:bg-gray-50 transition-colors py-3"
                >
                  <div className="flex flex-col items-center">
                    <div className={`relative w-10 h-10 rounded-full flex items-center justify-center ${isInDeleteMode ? 'bg-red-100' : iconBgClass}`}>
                      {isInDeleteMode ? (
                        <Trash2 className="w-5 h-5 text-red-600" />
                      ) : (
                        <>
                          <Icon className={`w-5 h-5 ${iconColorClass}`} />
                          {hasBadge && (
                            <span className="absolute -bottom-1 -right-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500 text-white border border-white">
                              <Check className="w-3 h-3" />
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    <span className="mt-2 text-xs font-medium text-gray-900 text-center">
                      {title}
                    </span>
                  </div>
                  <div className="mt-2 min-h-[32px] flex items-center justify-center px-1">
                    {isInDeleteMode ? (
                      <span className="text-[10px] leading-snug text-red-600 text-center break-words">
                        Toca para eliminar
                      </span>
                    ) : badgeText ? (
                      <span className="text-[10px] leading-snug text-emerald-600 text-center break-words">
                        {badgeText}
                      </span>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6">
          <h2 className="text-sm font-medium text-gray-500 mb-3 ml-2">Cantidad de imágenes</h2>
          <div className="border border-gray-200 rounded-2xl p-3 sm:p-4 bg-white">
            <div className="flex items-center justify-between sm:justify-start gap-3">
              <span className="text-sm text-gray-700">Cantidad</span>
              <div className="inline-flex items-center rounded-full border border-gray-200 bg-white shadow-sm">
                <button
                  type="button"
                  aria-label="Disminuir cantidad"
                  onClick={() => {
                    setImageCount(c => {
                      const next = Math.max(1, c - 1);
                      const qp = new URLSearchParams(location.search);
                      qp.set('cantidad_imagenes', String(next));
                      navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
                      return next;
                    });
                  }}
                  disabled={imageCount <= 1}
                  className="w-9 h-9 flex items-center justify-center rounded-full text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <div className="w-10 text-center text-sm text-gray-900 select-none">{imageCount}</div>
                <button
                  type="button"
                  aria-label="Aumentar cantidad"
                  onClick={() => {
                    setImageCount(c => {
                      const next = Math.min(8, c + 1);
                      const qp = new URLSearchParams(location.search);
                      qp.set('cantidad_imagenes', String(next));
                      navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
                      return next;
                    });
                  }}
                  disabled={imageCount >= 8}
                  className="w-9 h-9 flex items-center justify-center rounded-full text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <h2 className="text-sm font-medium text-gray-500 mb-3 ml-2">Formato</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {formatOptions.map(opt => {
              const selected = format === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => {
                    setFormat(opt.id);
                    const qp = new URLSearchParams(location.search);
                    qp.set('formato', opt.id);
                    navigate(`${location.pathname}?${qp.toString()}`, { replace: true });
                  }}
                  className={`w-full text-left border rounded-2xl p-2 sm:p-3 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                    selected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className="relative overflow-hidden rounded-xl">
                    <CachedImg
                      src={opt.image}
                      alt={opt.name}
                      className="w-full h-28 sm:h-32 object-cover"
                    />
                    {selected && (
                      <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white">
                        <Check className="w-4 h-4" />
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] sm:text-sm text-gray-900 mt-2 font-medium sm:font-semibold">
                    {opt.name}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      ) : (
        <div className="sm:bg-transparent">
          <GeneratedImagesInline
            imageIds={generatedImageIds}
            companyId={companyIdForImages}
            onReturnToConfig={handleReturnToConfig}
            onGenerateMore={handleGenerateMore}
            onReady={() => {
              setImagesReady(true);
              setLoadingExiting(true);
            }}
          />
        </div>
      )}

      {showLoadingScreen && (
        <LoadingAnimation
          message="Generando imágenes..."
          subMessage="Esto puede tardar unos segundos"
          durationMs={loadingDurationMs}
          exiting={loadingExiting}
          onExitComplete={() => setShowLoadingScreen(false)}
        />
      )}

      <SubscriptionLimitModal
        isOpen={showSubscriptionLimitModal}
        onClose={() => setShowSubscriptionLimitModal(false)}
        type={subscriptionLimitType}
      />
    </div>
  );
};

export default PruebaNuevaInterfaz;
